/** @jsx jsx */
import {
  useEffect,
  useState,
  createContext,
  useRef,
  useCallback,
  SetStateAction,
  Dispatch,
} from 'react';
import { jsx } from '@emotion/core';
import { Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Formik, Form, FormikValues } from 'formik';
import { AxiosPromise } from 'axios';

import { ChallengesAPI } from 'services/challenge.api';
import { userChallengeDeclaration } from 'actions/challenge.actions';
import { DeclarationDescription } from 'components';
import { StepType } from 'components/Stepper';
import { getChallengePhase } from 'core/utils/challenges.helpers';
import { CHALLENGE_PHASE, ROUTES } from 'constants/index';
import { ChallengeDatesType } from 'interfaces';
import { userInfosSelector } from 'reducers/app.reducer';
import { StyledContainer } from './styles';
import {
  getDates,
  getFormSteps,
  getSelectedProducts,
  makeInitialValues,
  makeValidationSchema,
} from './utils';
import { NavigationCheckDialog } from './dialogs/NavigationCheckDialog';
import { ConfirmDeclarationDialog } from './dialogs/ConfirmDeclarationDialog';
import { SubmittedDeclarationDialog } from './dialogs/SubmittedDeclarationDialog';
import { STEPS } from './constants';
import { IFormAnswer, IFormProduct } from './interfaces';
import { DeclarationFormProductsStep } from './DeclarationFormProductsStep';

type DeclarationContextType = {
  currentStepIndex: number;
  isFirstStep: boolean;
  goPrevStep: () => void;
  goNextStep: () => void;
  setOpenDeclarationConfirmationDialog: Dispatch<SetStateAction<boolean>>;
  openDeclarationConfirmationDialog: boolean;
};

export const DeclarationContext = createContext<DeclarationContextType>(
  undefined!,
);

const DeclarationForm = ({ challengeData }: { challengeData: any }) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [
    openDeclarationConfirmationDialog,
    setOpenDeclarationConfirmationDialog,
  ] = useState<boolean>(false);
  const [openSubmittedDeclarationDialog, setOpenSubmittedDeclarationDialog] =
    useState<boolean>(false);
  const [formiikValuesCopy, setFormiikValuesCopy] = useState<
    FormikValues | undefined
  >();

  const formiikRef: any = useRef(null);

  const dispatch = useDispatch();
  const { id: userId } = useSelector(userInfosSelector);

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const history = useHistory();
  const { id: challengeIdString }: { id: string } = useParams();
  const challengeId = Number(challengeIdString);

  const fetchDeclarations = useCallback(() => {
    if (challengeId) {
      dispatch(userChallengeDeclaration.request({ challengeId }));
    }
  }, [challengeId, dispatch]);

  const challengeDataAsString = JSON.stringify(challengeData);

  const declarationFormSteps = challengeData ? getFormSteps(challengeData) : [];
  const stepperSteps = declarationFormSteps.map(
    ({ stepper }: { stepper: StepType }) => stepper,
  );
  const DeclarationFormStep =
    declarationFormSteps?.[currentStep]?.component ||
    DeclarationFormProductsStep;

  const handleSubmit = (values: FormikValues): AxiosPromise => {
    const { declarationForm, needProof } = challengeData;

    const selectedProducts = getSelectedProducts(values[STEPS.products]).map(
      ({ id, selectedUnits, selectedValue }: IFormProduct) => ({
        challengeProduct: id,
        quantity: selectedUnits,
        ...(selectedValue && { currencyValue: selectedValue }),
      }),
    );
    const file = values[STEPS.file];
    const answers = values[STEPS.answers]?.map(
      ({ id, type, answer }: IFormAnswer) => ({
        formInput: id,
        ...(type === 'text'
          ? { value: answer }
          : { formInputChoiceValues: [{ formInputChoice: answer }] }),
      }),
    );

    const declaration = {
      challenge: challengeData['@id'],
      user: `/v2/users/${userId}`,
      challengeProductDeclarations: selectedProducts,
      ...(declarationForm && {
        formEntry: {
          form: declarationForm['@id'],
          formValues: answers,
        },
      }),
    };

    const formData = new FormData();

    if (needProof) {
      formData.append('file', file);
      formData.append('targetEntity', 'Declaration');
    }
    formData.append('declaration', JSON.stringify(declaration));

    return ChallengesAPI.postChallengeDeclaration(formData);
  };

  const isFirstStep = !currentStep;
  const goFirstStep = (): void => setCurrentStep(0);
  const goNextStep = (): void => setCurrentStep(currentStep + 1);
  const goPrevStep = (): void => setCurrentStep(currentStep - 1);

  const dates: ChallengeDatesType = getDates(challengeData, language);
  const challengePhase = getChallengePhase(dates);
  const isChallengeNotYetInDate =
    challengePhase === CHALLENGE_PHASE.BEFORE_CHALLENGE ||
    challengePhase === CHALLENGE_PHASE.BEFORE_DIFFUSION;

  // In order to not override the modified data, we cache the formiik values and patch
  // them on each data change
  useEffect(() => {
    setFormiikValuesCopy((formiikValuesCopy: FormikValues) => {
      const newValues = formiikRef.current?.values;
      const newProductsById = newValues?.[STEPS.products] ?? {};
      const oldProductById = formiikValuesCopy?.[STEPS.products] ?? {};
      return {
        ...newValues,
        [STEPS.products]: {
          ...oldProductById,
          ...newProductsById,
        },
      };
    });
  }, [challengeDataAsString]);

  return (
    <Formik
      innerRef={formiikRef}
      onSubmit={handleSubmit}
      initialValues={makeInitialValues(challengeData, formiikValuesCopy)}
      validationSchema={makeValidationSchema(challengeData)}
      enableReinitialize
    >
      <Form>
        <NavigationCheckDialog />
        <ConfirmDeclarationDialog
          open={openDeclarationConfirmationDialog}
          handleClose={() => setOpenDeclarationConfirmationDialog(false)}
          openSubmittedDeclarationDialog={() =>
            setOpenSubmittedDeclarationDialog(true)
          }
        />
        <SubmittedDeclarationDialog
          open={openSubmittedDeclarationDialog}
          handleClose={() => setOpenSubmittedDeclarationDialog(false)}
          goFirstStep={goFirstStep}
          redirect={() => history.push(`${ROUTES.CHALLENGE}/${challengeId}`)}
          isNewDeclarationPossible={challengeData.isDeclarationMultiple}
          fetchDeclarations={fetchDeclarations}
        />
        <DeclarationContext.Provider
          value={{
            currentStepIndex: currentStep,
            isFirstStep,
            goPrevStep,
            goNextStep,
            setOpenDeclarationConfirmationDialog,
            openDeclarationConfirmationDialog,
          }}
        >
          <StyledContainer>
            <DeclarationDescription
              challengeId={challengeId}
              currentStep={currentStep}
              steps={stepperSteps}
            />
            {isChallengeNotYetInDate ? (
              <Typography variant="body2" align="center" paragraph>
                {t('challenges.detail.description.declareResultsSoon')}
              </Typography>
            ) : (
              <DeclarationFormStep {...challengeData} />
            )}
          </StyledContainer>
        </DeclarationContext.Provider>
      </Form>
    </Formik>
  );
};

export default DeclarationForm;
